import { useState, useRef } from "react"
import { Upload, FileDown, Loader2, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import hive, { depositCash, withdrawCash } from "@/lib/api/hive"
import { validateCSV } from "@/lib/csv-parser"
import type { SkippedTransaction } from "@/types/portfolio"
import { Analytics } from "@/lib/analytics"

interface BulkTransactionDialogProps {
	portfolioId: string
	token: string
	cashCurrency?: string
	onSuccess: () => void
}

export function BulkTransactionDialog({ portfolioId, token, cashCurrency, onSuccess }: BulkTransactionDialogProps) {
	const [open, setOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [successCount, setSuccessCount] = useState<number | null>(null)
	const [skipped, setSkipped] = useState<SkippedTransaction[]>([])
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleDownloadSample = () => {
		const headers = ["ticker", "exchange", "type", "shares", "price", "date"]
		const sampleData = [
			["AAPL", "NASDAQ", "BUY", "10", "150.50", "2024-01-15"],
			["MSFT", "NASDAQ", "BUY", "5", "300.25", "2024-01-16"],
			["T", "NYSE", "BUY", "100", "18.50", "2024-01-17"],
			["NG", "LSE", "BUY", "200", "10.25", "2024-01-18"],
			["", "", "DEPOSIT", "5000", "1", "2024-01-01"],
		]

		const csvContent = [headers.join(","), ...sampleData.map((row) => row.join(","))].join("\n")

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
		const url = URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.setAttribute("href", url)
		link.setAttribute("download", "bulk_transactions_sample.csv")
		link.style.visibility = "hidden"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		setIsLoading(true)
		setError(null)
		setSuccessCount(null)

		const reader = new FileReader()
		reader.onload = async (e) => {
			try {
				const text = e.target?.result as string
				const allRows = validateCSV(text)

				const stockRows = allRows.filter((r) => r.type === "BUY" || r.type === "SELL") as {
					ticker: string
					exchange: string
					type: "BUY" | "SELL"
					shares: number
					price: number
					date: string
				}[]
				const cashRows = allRows.filter((r) => r.type === "DEPOSIT" || r.type === "WITHDRAWAL") as {
					type: "DEPOSIT" | "WITHDRAWAL"
					shares: number
					date: string
				}[]

				let totalImported = 0
				let allSkipped: SkippedTransaction[] = []

				if (stockRows.length > 0) {
					const result = await hive.bulkAddTransactions(portfolioId, stockRows, token)
					totalImported += result.transactions.length
					allSkipped = allSkipped.concat(result.skipped ?? [])
				}

				const cashCurrencyToUse = cashCurrency ?? "USD"
				for (const row of cashRows) {
					try {
						if (row.type === "DEPOSIT") {
							await depositCash(portfolioId, row.shares, cashCurrencyToUse, row.date)
						} else {
							await withdrawCash(portfolioId, row.shares, cashCurrencyToUse, row.date)
						}
						totalImported++
					} catch (err) {
						allSkipped.push({
							ticker: "",
							exchange: "",
							reason: err instanceof Error ? err.message : "Cash entry failed",
						})
					}
				}

				setSuccessCount(totalImported)
				setSkipped(allSkipped)
				Analytics.capture("Bulk Import Completed", { count: totalImported })
				onSuccess()

				// Keep dialog open for a moment to show success (longer if there are skipped items)
				const delay = allSkipped.length ? 5000 : 2000
				setTimeout(() => {
					setOpen(false)
					setSuccessCount(null)
					setSkipped([])
				}, delay)
			} catch (err: unknown) {
				setError(err instanceof Error ? err.message : "Failed to process CSV file")
				if (fileInputRef.current) fileInputRef.current.value = ""
			} finally {
				setIsLoading(false)
			}
		}
		reader.readAsText(file)
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(val) => {
				setOpen(val)
				if (!val) {
					setError(null)
					setSuccessCount(null)
					setSkipped([])
				}
			}}
		>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="text-sky-700 border-sky-200 hover:bg-sky-50 dark:text-sky-400 dark:border-sky-900/50 dark:hover:bg-sky-900/20"
				>
					<Upload className="h-4 w-4 mr-2" />
					Bulk Upload
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Bulk Import Transactions</DialogTitle>
					<DialogDescription>
						Upload a CSV file to add multiple transactions to your portfolio at once.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="bg-sky-50/50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-900/30 rounded-lg p-4 text-sm space-y-2">
						<h4 className="font-semibold text-sky-900 dark:text-sky-100">CSV Requirements:</h4>
						<ul className="list-disc list-inside text-muted-foreground space-y-1">
							<li>
								Required columns:{" "}
								<code className="bg-sky-100 dark:bg-sky-900/50 px-1 rounded">
									ticker, exchange, type, shares, price, date
								</code>
							</li>
							<li>
								Exchange: use the code shown in search results (e.g.{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">NASDAQ</code>,{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">NYSE</code>,{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">LSE</code>,{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">AS</code>)
							</li>
							<li>
								Type: <code className="font-medium text-sky-900 dark:text-sky-100">BUY</code>,{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">SELL</code>,{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">DEPOSIT</code>, or{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">WITHDRAWAL</code>
							</li>
							<li>
								For <code className="font-medium text-sky-900 dark:text-sky-100">DEPOSIT</code>/
								<code className="font-medium text-sky-900 dark:text-sky-100">WITHDRAWAL</code>: ticker
								and exchange can be empty; shares = amount
							</li>
							<li>
								Date format:{" "}
								<code className="font-medium text-sky-900 dark:text-sky-100">YYYY-MM-DD</code>
							</li>
						</ul>
						<Button
							variant="link"
							className="text-sky-600 p-0 h-auto text-sm"
							onClick={handleDownloadSample}
						>
							<FileDown className="h-4 w-4 mr-1" />
							Download sample CSV
						</Button>
					</div>

					{error && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertTitle>Error</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					{successCount !== null && (
						<Alert className="border-green-500 text-green-600 dark:text-green-400">
							<CheckCircle2 className="h-4 w-4 text-green-500" />
							<AlertTitle>Success</AlertTitle>
							<AlertDescription>
								Successfully imported {successCount} transaction{successCount !== 1 ? "s" : ""}.
							</AlertDescription>
						</Alert>
					)}

					{skipped.length > 0 && (
						<Alert className="border-amber-500 text-amber-700 dark:text-amber-400">
							<AlertTriangle className="h-4 w-4 text-amber-500" />
							<AlertTitle>
								{skipped.length} transaction{skipped.length !== 1 ? "s" : ""} skipped
							</AlertTitle>
							<AlertDescription>
								<ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
									{skipped.map((s, i) => (
										<li key={i}>
											{s.ticker} ({s.exchange}): {s.reason}
										</li>
									))}
								</ul>
							</AlertDescription>
						</Alert>
					)}

					<div className="flex flex-col items-center justify-center border-2 border-dashed border-sky-200 dark:border-sky-900/50 rounded-xl p-8 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-colors">
						<input
							type="file"
							accept=".csv"
							className="hidden"
							ref={fileInputRef}
							onChange={handleFileUpload}
							disabled={isLoading}
						/>
						<Upload className="h-10 w-10 text-sky-600 mb-4 opacity-50" />
						<Button
							onClick={() => fileInputRef.current?.click()}
							disabled={isLoading}
							className="bg-sky-600 hover:bg-sky-700 text-white"
						>
							{isLoading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Processing...
								</>
							) : (
								"Select CSV File"
							)}
						</Button>
						<p className="mt-2 text-xs text-muted-foreground">Limit: 5MB per file</p>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
